import { memo, useCallback, useRef, useEffect, useState, useMemo } from 'react';
import { Handle, NodeProps, Position } from '@xyflow/react';
import { TextCursorInput } from 'lucide-react';
import { NodeSelector } from '@/pages/workflow/component/controls/node-selector';
import { useNodeStore, updatePortOnEdgeDelete } from '@/stores/node-store';
import { useWorkflowValidationStore } from '@/stores/workflow-validation-store';
import { useWorkflowPanelStore } from '@/stores/workflow-panel-store';
import {
  NodeStruct,
  NodeType,
  CustomNode,
  HandleType,
  NodeParam,
  NodeInputPort,
  NodeData,
  NodeCategory,
  SELECT_NODE_PLACEHOLDER_STRUCT,
} from '@/types/node-types';
import { ConnectionType, CustomEdge } from '@/types/edge-types';
import { useTheme } from '@/providers/theme-provider';
import {
  HANDLE_STYLING,
  HandleTypes,
  getCategoryConfig,
} from '@/constants/node-styling';
import {
  generateAutoNodeName,
  isNameParameter,
} from '@/stores/helpers/node-naming';
import { VERTICAL_STACK_GAP } from '@/stores/helpers/node-drop-helpers';
import './style.css';

// SELECT_NODE_PLACEHOLDER_STRUCT is exported from @/types/node-types and
// re-exported here for backward compatibility.
export { SELECT_NODE_PLACEHOLDER_STRUCT };

interface SelectNodeData extends NodeData {
  type?: 'source' | 'target';
  targetNodeId?: string;
  targetHandleId?: string;
  /** Import validation: clear when user picks a replacement (see errors-tab Replace Node) */
  pendingUnsupportedErrorId?: string;
}

const SelectNode: React.FC<NodeProps> = ({ id, data }) => {
  const nodeData = data as SelectNodeData;
  const {
    nodes,
    setNodes,
    edges,
    setEdges,
    onNodesEdgesDelete,
    isLoadingNodeList,
    selectNode,
  } = useNodeStore();
  const { openWorkflowPanel, setHighlightedField, setConsolePanelOpen } =
    useWorkflowPanelStore();
  const { theme } = useTheme();
  const selectNodeRef = useRef<HTMLDivElement>(null);
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const [isNewlyCreated, setIsNewlyCreated] = useState(true);
  const hasTransformedRef = useRef(false);
  const addedCountRef = useRef(0);

  // Determine if this SelectNode is a source (created from input port drag)
  const isSourceNode = nodeData.type === 'target';

  // Grace period for newly created select nodes
  useEffect(() => {
    if (isNewlyCreated) {
      const timer = setTimeout(() => {
        setIsNewlyCreated(false);
      }, 500); // 500ms grace period for newly created nodes

      return () => clearTimeout(timer);
    }
  }, [isNewlyCreated]);

  // Additional check: if this select node has incoming edges, it was likely created from edge connection
  useEffect(() => {
    const hasIncomingEdges = edges.some((edge) => edge.target === id);
    if (hasIncomingEdges && isNewlyCreated) {
      // If there are incoming edges and it's newly created, extend the grace period
      setIsNewlyCreated(true);
    }
  }, [edges, id, isNewlyCreated]);

  // Handle ESC key to remove select node
  // TODO: use useKeyboardShortcuts hook instead
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' || event.keyCode === 27) {
        onNodesEdgesDelete({ nodeId: id });
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [id, onNodesEdgesDelete]);

  // Handle click outside to remove select node
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Add a small delay to allow React Flow to process its events first
      setTimeout(() => {
        if (
          selectNodeRef.current &&
          !selectNodeRef.current.contains(event.target as Node) &&
          !isUserInteracting &&
          !isNewlyCreated // Don't remove if node was just created
        ) {
          // Remove the select node if user clicked outside and wasn't interacting
          // This includes clicks on the canvas, other nodes, or any other area
          onNodesEdgesDelete({ nodeId: id });
        }
      }, 10); // Small delay to let React Flow process first
    };

    // Add event listeners for different types of interactions
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('click', handleClickOutside);

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [id, onNodesEdgesDelete, isUserInteracting, isNewlyCreated]);

  // Reset interaction state after a delay
  useEffect(() => {
    if (isUserInteracting) {
      const timer = setTimeout(() => {
        setIsUserInteracting(false);
      }, 1000); // Reset after 1 second of no interaction

      return () => clearTimeout(timer);
    }
  }, [isUserInteracting]);

  // Find the target node and param field for "Write to" option
  const writeToOption = useMemo(() => {
    if (!isSourceNode || !nodeData.targetNodeId || !nodeData.targetHandleId) {
      return null;
    }

    // Find the target node
    const targetNode = nodes.find((n) => n.id === nodeData.targetNodeId);
    if (!targetNode?.data?.struct) return null;

    const targetStruct = targetNode.data.struct as NodeStruct;

    // Find the param field that matches the targetHandleId
    const paramField = targetStruct.params.find(
      (param) =>
        param.field_id === nodeData.targetHandleId ||
        param.bindable_config?.input_port?.id === nodeData.targetHandleId,
    );

    // Only show if the field is visible (show: true)
    if (!paramField || !paramField.show) return null;

    // Get the target node's category for styling
    const category = targetStruct.node_type as NodeCategory;

    return {
      paramField,
      targetNodeId: nodeData.targetNodeId,
      category,
    };
  }, [isSourceNode, nodeData.targetNodeId, nodeData.targetHandleId, nodes]);

  // Handler for "Write to" option click
  const handleWriteToClick = useCallback(() => {
    if (!writeToOption) return;

    const { paramField, targetNodeId } = writeToOption;

    // Find edges connected to the SelectNode that need cleanup
    const edgesToDelete = edges.filter(
      (edge) => edge.source === id || edge.target === id,
    ) as CustomEdge[];

    // Remove the SelectNode from nodes
    const nodesWithoutSelectNode = nodes.filter((node) => node.id !== id);

    // Clean up port connections on target nodes (clears incoming_connections, bindable_config.is_bound)
    const nodesWithCleanedPorts = updatePortOnEdgeDelete(
      edgesToDelete,
      nodesWithoutSelectNode,
    );

    // Set selected state for target node
    const updatedNodes = nodesWithCleanedPorts.map((node) => ({
      ...node,
      selected: node.id === targetNodeId,
    }));

    // Remove edges connected to the SelectNode
    const updatedEdges = edges.filter(
      (edge) => edge.source !== id && edge.target !== id,
    );

    setNodes(updatedNodes);
    setEdges(updatedEdges);
    selectNode(targetNodeId);

    const validation = useWorkflowValidationStore.getState();
    [...validation.unsupportedNodes]
      .filter((e) => e.nodeId === id)
      .forEach((e) => validation.removeUnsupportedNode(e.id));

    // NOTE: setTimeout ensures the code below runs after the setNodes, setEdges, and selectNode calls above
    setTimeout(() => {
      // Open the console panel and configure tab with node data
      setConsolePanelOpen(true);
      openWorkflowPanel('configure', { nodeId: targetNodeId });
      setHighlightedField(targetNodeId, paramField.field_id);
    }, 0);
  }, [
    writeToOption,
    id,
    nodes,
    edges,
    setNodes,
    setEdges,
    selectNode,
    setConsolePanelOpen,
    openWorkflowPanel,
    setHighlightedField,
  ]);

  // Create the customAction for the NodeSelector
  const customAction = useMemo(() => {
    if (!writeToOption || !nodeData.targetHandleId) return undefined;

    const fieldDisplayName =
      writeToOption.paramField.display_name ||
      writeToOption.paramField.field_id;

    // Get category-specific styling to match slot-card appearance
    const config = getCategoryConfig(
      writeToOption.category,
      nodeData.targetHandleId,
      fieldDisplayName,
    );

    return {
      icon: TextCursorInput,
      label: `Write a ${fieldDisplayName}`,
      onClick: handleWriteToClick,
      primaryColor: config.styling.primaryColor,
    };
  }, [writeToOption, nodeData.targetHandleId, handleWriteToClick]);

  // Create a new node via addNode and connect it to the peer node (source or target)
  // that the original SelectNode was connected to. Used for 2nd+ nodes in multi-add.
  const addExtraNode = useCallback(
    (nodeStruct: NodeStruct) => {
      const {
        addNode,
        onConnect: storeOnConnect,
        isValidEdgeConnection,
        nodes: freshNodes,
        edges: freshEdges,
        setNodes,
      } = useNodeStore.getState();
      const acceptedTypes = nodeData.acceptedTypes || [];

      // Resolve edge and handles BEFORE creating the node so a missing edge or
      // unresolvable handle bails out cleanly without leaving an orphaned node.
      let pendingConnection: {
        source: string | null;
        target: string | null;
        sourceHandle: string;
        targetHandle: string;
      };

      if (isSourceNode) {
        const originalEdge = freshEdges.find((edge) => edge.source === id);
        if (!originalEdge) return;

        const sourceOutputPort = nodeStruct.output_ports.find((port) =>
          acceptedTypes.some((type: string) =>
            port.port_type.accepted_types.includes(type as HandleType),
          ),
        );
        const sourcePortId =
          sourceOutputPort?.id || nodeStruct.output_ports[0]?.id;
        if (!sourcePortId) return;

        pendingConnection = {
          source: null, // filled with newNodeId after addNode
          target: originalEdge.target,
          sourceHandle: sourcePortId,
          targetHandle: originalEdge.targetHandle || '',
        };
      } else {
        const originalEdge = freshEdges.find((edge) => edge.target === id);
        if (!originalEdge) return;

        const bindableParam = nodeStruct.params.find((param) =>
          acceptedTypes.some((type: string) =>
            param.bindable_config?.input_port?.port_type.accepted_types.includes(
              type as HandleType,
            ),
          ),
        );
        const bindableInputPort = nodeStruct.input_ports.find(
          (port: NodeInputPort) =>
            acceptedTypes.some((type: string) =>
              port.port_type.accepted_types.includes(type as HandleType),
            ),
        );
        const targetPortId =
          bindableParam?.bindable_config?.input_port?.id ||
          bindableInputPort?.id;
        if (!targetPortId) return;

        pendingConnection = {
          source: originalEdge.source,
          target: null, // filled with newNodeId after addNode
          sourceHandle: originalEdge.sourceHandle || '',
          targetHandle: targetPortId,
        };
      }

      const selectNodeEntry = freshNodes.find((n) => n.id === id);
      const basePosition = selectNodeEntry?.position ?? { x: 0, y: 0 };

      addedCountRef.current += 1;
      const newPosition = {
        x: basePosition.x,
        y: basePosition.y + addedCountRef.current * VERTICAL_STACK_GAP,
      };

      // Add unselected so an invalid-connection cleanup below doesn't leave
      // `lastSelectedNodeId` pointing at a node we're about to remove (and
      // doesn't churn the previous selection state). On success we explicitly
      // select the new node after the connection lands so the configure panel
      // surfaces the latest add — matching the SlotCard multi-add behavior.
      const newNodeId = addNode(nodeStruct, newPosition, { selected: false });

      const connection = {
        source: pendingConnection.source ?? newNodeId,
        target: pendingConnection.target ?? newNodeId,
        sourceHandle: pendingConnection.sourceHandle,
        targetHandle: pendingConnection.targetHandle,
      };

      if (!isValidEdgeConnection(connection)) {
        // Remove the node we just added — the connection is invalid (e.g. cardinality one).
        const { nodes: currentNodes } = useNodeStore.getState();
        setNodes(currentNodes.filter((n) => n.id !== newNodeId));
        addedCountRef.current -= 1;
        return;
      }

      storeOnConnect(connection);
      useNodeStore.getState().selectNode(newNodeId);
    },
    [id, nodeData.acceptedTypes, isSourceNode],
  );

  // transform the SelectNode to a GenericNode and connect it to the selected node
  const handleNodeClick = useCallback(
    (nodeStruct: NodeStruct) => {
      // After first transform, subsequent calls create additional nodes
      if (hasTransformedRef.current) {
        addExtraNode(nodeStruct);
        return;
      }
      hasTransformedRef.current = true;

      const { addParamPortConn, addAgentHandlePortConn } =
        useNodeStore.getState();

      // Generate auto-name if applicable (e.g., for Agent nodes)
      const autoName = generateAutoNodeName(nodeStruct, nodes);

      // Apply auto-name and default values to params (same logic as createNodeWithSyncedId)
      const updatedParams = nodeStruct.params.map((param) => {
        // 1. Set name parameters
        if (
          isNameParameter(param, nodeStruct) &&
          (param.value === '' ||
            param.value === undefined ||
            param.value === null)
        ) {
          // For AGENT nodes, use auto-generated name with number (e.g., "Agent 1")
          // For targeted governance nodes, just use the display_name without number
          const nameValue =
            nodeStruct.node_type === NodeCategory.AGENT
              ? autoName
              : nodeStruct.display_name;
          if (nameValue) {
            return { ...param, value: nameValue };
          }
        }

        // 2. Apply default values to empty fields
        if (
          (param.value === undefined ||
            param.value === null ||
            param.value === '') &&
          param.default !== undefined &&
          param.default !== null &&
          param.default !== '' &&
          (param.required ||
            param.advanced ||
            (typeof param.default === 'string' && param.default.trim() !== ''))
        ) {
          return { ...param, value: param.default };
        }

        return param;
      });

      // For AGENT nodes, set user_config_name to auto-generated name (shows in node title)
      // For targeted governance nodes (Evaluations, Text Redaction, PDF Redaction),
      // only pre-fill the input field - don't change the node title
      const shouldSetUserConfigName =
        autoName && nodeStruct.node_type === NodeCategory.AGENT;

      // update current SelectNode with the GenericNode user picked
      let updatedNodes: Array<CustomNode> = nodes.map((node) => {
        if (node.id === id) {
          const newNode = {
            id: id,
            type: NodeType.GenericNode,
            position: node.position,
            data: {
              struct: {
                ...nodeStruct,
                react_flow_generated_id: id,
                user_config_name: shouldSetUserConfigName
                  ? autoName
                  : nodeStruct.user_config_name,
                params: updatedParams,
              },
            },
            selected: true,
          };
          return newNode;
        }
        return node;
      });

      // find out what's the accepted types of connection
      const acceptedTypes = nodeData.acceptedTypes || [];

      // if SelectNode is the source
      if (isSourceNode) {
        // Find the first output port of the selected node that matches accepted types
        const sourceOutputPort = nodeStruct.output_ports.find((port) =>
          acceptedTypes.some((type: string) =>
            port.port_type.accepted_types.includes(type as HandleType),
          ),
        );

        const sourcePortId =
          sourceOutputPort?.id || nodeStruct.output_ports[0]?.id;

        // Find the outgoing edge to get target info
        const outgoingEdge = edges.find((edge) => edge.source === id);
        const targetNode = outgoingEdge
          ? updatedNodes.find((node) => node.id === outgoingEdge.target)
          : null;

        // Check if target is a param input port
        let targetParamId: string | null = null;
        if (outgoingEdge?.targetHandle && targetNode?.data.struct.params) {
          const matchingParam = targetNode.data.struct.params.find(
            (param) =>
              param.bindable_config?.input_port?.id ===
              outgoingEdge.targetHandle,
          );
          if (matchingParam) {
            targetParamId = matchingParam.field_id;
          }
        }

        const isPortToParam = targetParamId !== null;

        // Update the source node and port for the outgoing edge
        const updatedEdges = edges.map((edge) => {
          if (edge.source === id) {
            return {
              ...edge,
              sourceNodeId: nodeStruct.node_id,
              source_node_id: id,
              source_port_id: sourcePortId,
              sourceHandle: sourcePortId || '',
              ...(isPortToParam && { target_param_id: targetParamId }),
              edge_type: isPortToParam
                ? ConnectionType.PORT_TO_PARAM
                : ConnectionType.PORT_TO_PORT,
            } as CustomEdge;
          }
          return edge;
        });
        setEdges(updatedEdges);

        // Update bindable config for the target node
        if (outgoingEdge) {
          const sourceNode = updatedNodes.find((node) => node.id === id);

          if (
            sourceNode &&
            targetNode &&
            outgoingEdge.targetHandle &&
            isPortToParam
          ) {
            updatedNodes = addParamPortConn(
              updatedNodes,
              outgoingEdge.target, // target reactflow node id
              outgoingEdge.targetHandle,
              outgoingEdge.source || id, // source reactflow node id
              sourcePortId || '',
            );

            // Clear validation error for the connected field
            if (targetParamId && targetNode.data.struct.params) {
              const targetParam = targetNode.data.struct.params.find(
                (p) => p.field_id === targetParamId,
              );
              if (targetParam) {
                const fieldId = targetParam.field_id;
                useWorkflowValidationStore
                  .getState()
                  .clearFieldError(outgoingEdge.target, fieldId);
              }
            }
          }

          if (
            sourceNode?.data.struct.node_type === NodeCategory.TOOL &&
            targetNode?.data.struct.node_type === NodeCategory.AGENT
          ) {
            const connectionParams = {
              source: id,
              target: outgoingEdge.target,
              sourceHandle: sourcePortId,
              targetHandle: outgoingEdge.targetHandle,
            };
            updatedNodes = addAgentHandlePortConn(
              updatedNodes,
              connectionParams,
              targetNode,
            );
          }
        }
      } else {
        // If SelectNode is the target
        // Find the first bindable param of the target nodeStruct that can accept one of the accepted types
        const bindableParam: NodeParam | undefined = nodeStruct.params.find(
          (param) =>
            acceptedTypes.some((type: string) =>
              param.bindable_config?.input_port?.port_type.accepted_types.includes(
                type as HandleType,
              ),
            ),
        );
        const isPortToParam = !!bindableParam?.bindable_config?.input_port?.id;

        // Find the first bindable input of the target nodeStruct that can accept one of the accepted types
        const bindableInputPort = nodeStruct.input_ports.find(
          (port: NodeInputPort) =>
            acceptedTypes.some((type: string) =>
              port.port_type.accepted_types.includes(type as HandleType),
            ),
        );

        const targetPortId =
          bindableParam?.bindable_config?.input_port?.id ||
          bindableInputPort?.id;

        // update the target node and port for the connected edge
        const updatedEdges = edges.map((edge) => {
          if (edge.target === id) {
            return {
              ...edge,
              targetNodeId: nodeStruct.node_id,
              target_node_id: id,
              target_port_id: targetPortId,
              targetHandle: targetPortId || '',
              ...(isPortToParam && {
                target_param_id: bindableParam?.field_id,
              }),
              edge_type: isPortToParam
                ? ConnectionType.PORT_TO_PARAM
                : ConnectionType.PORT_TO_PORT,
            } as CustomEdge;
          }
          return edge;
        });
        setEdges(updatedEdges);

        // Find the source node and connection information from edges
        const incomingEdge = edges.find((edge) => edge.target === id);
        if (incomingEdge) {
          const sourceNode = updatedNodes.find(
            (node) => node.id === incomingEdge.source,
          );
          const targetNode = updatedNodes.find((node) => node.id === id);

          if (sourceNode && targetNode) {
            // Update bindable config for the target field if it has one
            if (nodeStruct && targetPortId && isPortToParam) {
              // Chain updates: addParamPortConn returns updated nodes
              updatedNodes = addParamPortConn(
                updatedNodes,
                incomingEdge.target || id, // target reactflow node id
                targetPortId,
                incomingEdge.source, // source reactflow node id
                incomingEdge.sourceHandle!,
              );

              // Clear validation error for the connected field
              if (bindableParam) {
                const fieldId = bindableParam.field_id;
                useWorkflowValidationStore
                  .getState()
                  .clearFieldError(incomingEdge.target || id, fieldId);
              }
            }

            if (
              sourceNode?.data.struct.node_type === NodeCategory.TOOL &&
              targetNode?.data.struct.node_type === NodeCategory.AGENT
            ) {
              // Update agent node when a tool is connected
              const connectionParams = {
                source: incomingEdge.source,
                target: incomingEdge.target,
                sourceHandle: incomingEdge.sourceHandle,
                targetHandle: incomingEdge.targetHandle,
              };
              // Chain updates: addAgentHandlePortConn returns updated nodes
              updatedNodes = addAgentHandlePortConn(
                updatedNodes,
                connectionParams,
                targetNode,
              );
            }
          }
        }
      }

      setNodes(updatedNodes);

      if (nodeData.pendingUnsupportedErrorId) {
        useWorkflowValidationStore
          .getState()
          .removeUnsupportedNode(nodeData.pendingUnsupportedErrorId);
      }

      const { setActiveTab } = useWorkflowPanelStore.getState();
      const { selectNode: storeSelectNode } = useNodeStore.getState();
      storeSelectNode(id);
      setActiveTab('configure');
    },
    [
      nodeData,
      edges,
      id,
      setEdges,
      setNodes,
      nodes,
      isSourceNode,
      addExtraNode,
    ],
  );

  // Handle user interaction with the select node
  const handleUserInteraction = useCallback(() => {
    setIsUserInteracting(true);
    setIsNewlyCreated(false); // Reset newly created state when user interacts
  }, []);

  // Determine handle color based on connected node
  const handleColor = useMemo(() => {
    const defaultColor = HANDLE_STYLING[HandleTypes.PARAM].color;

    let nodeType: NodeCategory;
    if (isSourceNode) {
      // Select node is acting as source, find the outgoing edge and target node
      const outgoingEdge = edges.find((edge) => edge.source === id);
      if (!outgoingEdge) {
        return defaultColor;
      }

      // Find the target node
      const targetNode = nodes.find((n) => n.id === outgoingEdge.target);
      if (!targetNode || !targetNode.data?.struct?.node_type) {
        return defaultColor;
      }

      nodeType = targetNode.data.struct.node_type as NodeCategory;
    } else {
      // Select node is acting as target, find the incoming edge and source node
      const incomingEdge = edges.find((edge) => edge.target === id);
      if (!incomingEdge) {
        return defaultColor;
      }

      // Find the source node
      const sourceNode = nodes.find((n) => n.id === incomingEdge.source);
      if (!sourceNode || !sourceNode.data?.struct?.node_type) {
        return defaultColor;
      }

      nodeType = sourceNode.data.struct.node_type as NodeCategory;
    }

    switch (nodeType) {
      case NodeCategory.TOOL:
        return HANDLE_STYLING[HandleTypes.TOOL].color;
      case NodeCategory.MEMORY:
        return HANDLE_STYLING[HandleTypes.MEMORY].color;
      default:
        return HANDLE_STYLING[HandleTypes.PARAM].color;
    }
  }, [id, edges, nodes, isSourceNode]);

  return (
    <div
      ref={selectNodeRef}
      data-testid='select-node'
      className='select-node w-[350px] rounded-[8px] p-0 pb-2 bg-card border border-border shadow-sm'
      style={{
        boxShadow:
          '0px 2px 4px -1px rgba(0, 0, 0, 0.06), 0px 4px 6px -1px rgba(0, 0, 0, 0.1)',
      }}
      onMouseDown={handleUserInteraction}
      onClick={handleUserInteraction}
    >
      <Handle
        type={isSourceNode ? 'source' : 'target'}
        position={isSourceNode ? Position.Right : Position.Left}
        id={
          isSourceNode
            ? 'select-node-source-handle'
            : 'select-node-target-handle'
        }
        isConnectable={true}
        style={{
          backgroundColor: handleColor,
          borderColor: 'var(--color-border)',
          borderWidth: '2px',
          borderStyle: 'solid',
        }}
      />
      {/* nowheel class allows scrolling within a node */}
      <div
        className='nowheel pt-2'
        onMouseDown={handleUserInteraction}
        onClick={handleUserInteraction}
        onKeyDown={handleUserInteraction}
        onInput={handleUserInteraction}
      >
        <NodeSelector
          nodes={(data.compatibleNodes as NodeStruct[]) || []}
          primaryColor={theme === 'dark' ? '#9ca3af' : '#4b5563'}
          onNodeClick={handleNodeClick}
          placeholder='Search nodes...'
          maxHeight='max-h-[300px]'
          listTitle='Compatible nodes'
          isLoading={isLoadingNodeList}
          customAction={customAction}
        />
      </div>
    </div>
  );
};

export default memo(SelectNode);
